import React, { useState, useMemo } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import { useToast } from '../../components/Toast/ToastContext';
import { checkResellerChipAccessQuery } from '@/appview/RegisterChipV2/RegisterChip.graphql';
import { allBreedsQuery } from '@/appview/RegisterChipV2/PetInfoStepV2/BreedSelect.graphql';
import { rescuePetsQuery, RescuePetsData, RescuePetGql } from '../../lib/rescueQueries';
import { Species, Gender } from '@/types/gql-op-types';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import StepProgress from '../../components/StepProgress/StepProgress';
import Button from '../../components/Button/Button';
import { PortalInput, PortalTextarea } from '../../components/FormField/PortalInput';
import { PortalSelect } from '../../components/FormField/PortalSelect';
import { PortalCombobox } from '../../components/FormField/PortalCombobox';
import { PortalField } from '../../components/FormField/PortalField';
import { ToggleGroup } from '../../components/FormField/ToggleGroup';
import { CheckboxInline } from '../../components/FormField/CheckboxInline';
import BreedSelectModal from '@/appview/RegisterChipV2/PetInfoStepV2/BreedSelectModal';
import { FormGrid } from '../../components/FormField/FormGrid';
import ConfirmSummary from '../../components/ConfirmSummary/ConfirmSummary';
import { AdopterFormData, INITIAL_ADOPTER } from './TransferChipModal';
import { KG_TO_LBS, US_STATES } from '../../lib/utils';
import styles from './Chips.module.scss';

export interface RegisterChipPayload {
  graphqlId: string;
  chipId: string;
  serialNumber: string;
  needsClaim: boolean;
  petData?: PetFormData;
  breedId?: string | null;
  adopterData?: AdopterFormData;
  existingPetId?: string;
}

interface RegisterChipModalProps {
  open: boolean;
  onClose: () => void;
  initialChipId?: string;
  onRegister: (payload: RegisterChipPayload) => Promise<void>;
}

export interface PetFormData {
  petName: string;
  species: string;
  breed: string;
  mixedBreed: boolean;
  gender: string;
  dobYear: string;
  dobMonth: string;
  dobDay: string;
  spayedNeutered: string;
  color: string;
  weight: string;
  additionalInfo: string;
}

interface ExistingPetInfo {
  name: string;
  species: string;
  gender: string;
  breedName: string;
  isPurebred: boolean;
  color: string;
  dob: string;
  weight: string;
}

const EMPTY_EXISTING_PET: ExistingPetInfo = {
  name: '',
  species: '',
  gender: '',
  breedName: '',
  isPurebred: true,
  color: '',
  dob: '',
  weight: '',
};

const INITIAL_PET: PetFormData = {
  petName: '',
  species: 'dog',
  breed: '',
  mixedBreed: false,
  gender: '',
  dobYear: '',
  dobMonth: '',
  dobDay: '',
  spayedNeutered: '',
  color: '',
  weight: '',
  additionalInfo: '',
};

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [
  { value: '', label: 'Year' },
  ...Array.from({ length: CURRENT_YEAR - 2001 + 1 }, (_, i) => {
    const y = String(CURRENT_YEAR - i);
    return { value: y, label: y };
  }),
];

const MONTHS = [
  { value: '', label: 'Month' },
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const DAYS = [
  { value: '', label: 'Day' },
  ...Array.from({ length: 31 }, (_, i) => {
    const d = String(i + 1).padStart(2, '0');
    return { value: d, label: String(i + 1) };
  }),
];

function speciesFormToGql(species: string): Species | undefined {
  if (species === 'dog') return Species.DOG;
  if (species === 'cat') return Species.CAT;
  if (species === 'other') return Species.OTHER;
  return undefined;
}

function gqlSpeciesToDisplay(species?: Species | null): string {
  if (species === Species.DOG) {
    return 'dog';
  }
  if (species === Species.CAT) {
    return 'cat';
  }
  if (species === Species.OTHER) {
    return 'other';
  }
  return '';
}

function gqlGenderToDisplay(gender?: Gender | null): string {
  if (gender === Gender.MALE) {
    return 'male';
  }
  if (gender === Gender.FEMALE) {
    return 'female';
  }
  return '';
}

function formatDobParts(year?: number | null, month?: number | null, day?: number | null): string {
  if (!year) {
    return '';
  }
  const parts = [String(year)];
  if (month) {
    parts.unshift(String(month).padStart(2, '0'));
  }
  if (day) {
    parts.splice(month ? 1 : 0, 0, String(day).padStart(2, '0'));
  }
  return parts.join('/');
}

export default function RegisterChipModal({ open, onClose, initialChipId, onRegister }: RegisterChipModalProps) {
  const { showToast } = useToast();
  const [step, setStep] = useState(1);
  const [chipId, setChipId] = useState(initialChipId || '');
  const [chipError, setChipError] = useState('');
  const [validating, setValidating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [graphqlId, setGraphqlId] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [needsClaim, setNeedsClaim] = useState(false);
  const [hasPet, setHasPet] = useState(false);
  const [existingPet, setExistingPet] = useState<ExistingPetInfo>(EMPTY_EXISTING_PET);
  const [pet, setPet] = useState<PetFormData>({ ...INITIAL_PET });
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [adopter, setAdopter] = useState<AdopterFormData>({ ...INITIAL_ADOPTER });
  const [adopterErrors, setAdopterErrors] = useState<Record<string, boolean>>({});
  const [petMode, setPetMode] = useState<'new' | 'existing'>('new');
  const [selectedExistingPetId, setSelectedExistingPetId] = useState('');
  const [existingPetError, setExistingPetError] = useState('');

  const [checkChipAccess] = useLazyQuery(checkResellerChipAccessQuery);

  // Pets in the rescue's household that don't yet have a chip — eligible
  // targets for the "use existing pet" branch of step 2. `cache-first` mirrors
  // Chips.tsx so opening the modal doesn't always trigger a network request.
  const { data: rescuePetsData } = useQuery<RescuePetsData>(rescuePetsQuery, {
    fetchPolicy: 'cache-first',
  });
  const rescuePetOptions: RescuePetGql[] = useMemo(() => {
    const rescueHousehold = rescuePetsData?.currentUser?.userHouseholds?.find((uh) => uh.household.type === 'RESCUE');
    const pets = rescueHousehold?.household.pets ?? [];
    return pets.filter((p) => !p.chip).sort((a, b) => a.name.localeCompare(b.name));
  }, [rescuePetsData]);
  // Combobox options — breed disambiguates duplicate names, which are common
  // in rescues. Mirrors AddDeviceToPetModal so both flows feel the same.
  const existingPetComboboxOptions = useMemo(
    () =>
      rescuePetOptions.map((p) => ({
        value: p.id,
        label: p.breed?.name ? `${p.name} — ${p.breed.name}` : p.name,
      })),
    [rescuePetOptions],
  );
  const selectedExistingPet = useMemo(
    () => rescuePetOptions.find((p) => p.id === selectedExistingPetId) ?? null,
    [rescuePetOptions, selectedExistingPetId],
  );

  // Load breeds for the currently selected species
  const gqlSpecies = speciesFormToGql(pet.species);
  const { data: breedsData } = useQuery(allBreedsQuery, {
    variables: { species: gqlSpecies },
    skip: !gqlSpecies,
  });

  const breedsList = useMemo(() => breedsData?.allBreeds ?? [], [breedsData]);
  const breedOptions = useMemo(
    () => breedsList.map((b: { id: string; name: string }) => ({ value: b.id, label: b.name })),
    [breedsList],
  );

  function reset() {
    setStep(1);
    setChipId(initialChipId || '');
    setChipError('');
    setValidating(false);
    setSubmitting(false);
    setGraphqlId('');
    setSerialNumber('');
    setNeedsClaim(false);
    setHasPet(false);
    setExistingPet(EMPTY_EXISTING_PET);
    setPet({ ...INITIAL_PET });
    setErrors({});
    setAdopter({ ...INITIAL_ADOPTER });
    setAdopterErrors({});
    setPetMode('new');
    setSelectedExistingPetId('');
    setExistingPetError('');
  }

  function handleClose() {
    reset();
    onClose();
  }

  // Step 1: Validate chip ID via API
  async function validateChipId() {
    const trimmed = chipId.trim();
    if (!trimmed) {
      setChipError('Please enter a chip ID.');
      return;
    }
    setValidating(true);
    setChipError('');
    try {
      const { data, error } = await checkChipAccess({ variables: { id: trimmed } });
      if (error || !data?.resellerChip) {
        setChipError('Chip not found in your inventory.');
        return;
      }
      const nextGraphqlId = data.resellerChip.id;
      // Preserve any half-filled pet form when the user taps Back from step 2
      // and re-validates the same chip. Only wipe when the chip actually
      // changed, so a stale existing-pet selection or new-pet form from a
      // *different* chip can't silently carry over.
      const sameChip = !!graphqlId && nextGraphqlId === graphqlId;
      setGraphqlId(nextGraphqlId);
      setSerialNumber(data.resellerChip.serialNumber);
      const chipResellerId = data.resellerChip.reseller?.id;
      const userResellerId = data.currentUser?.chipReseller?.id;
      setNeedsClaim(!chipResellerId || chipResellerId !== userResellerId);
      const petData = data.resellerChip.pet;
      const chipHasPet = !!petData;
      setHasPet(chipHasPet);
      if (petData) {
        setExistingPet({
          name: petData.name ?? '',
          species: gqlSpeciesToDisplay(petData.species),
          gender: gqlGenderToDisplay(petData.gender),
          breedName: petData.breed?.name ?? '',
          isPurebred: petData.isPurebred ?? true,
          color: petData.color ?? '',
          dob: formatDobParts(petData.yearOfBirth, petData.monthOfBirth, petData.dayOfBirth),
          weight: petData.weight ? `${Math.round(petData.weight * KG_TO_LBS)} lbs` : '',
        });
      }
      if (!sameChip) {
        setPetMode('new');
        setSelectedExistingPetId('');
        setExistingPetError('');
        setPet({ ...INITIAL_PET });
        setErrors({});
      }
      setStep(chipHasPet ? 3 : 2);
    } catch {
      setChipError('Chip not found in your inventory.');
    } finally {
      setValidating(false);
    }
  }

  // Step 2: Validate pet info
  function validatePet(): boolean {
    const errs: Record<string, boolean> = {};
    if (!pet.petName.trim()) errs.petName = true;
    if (!pet.gender) errs.gender = true;
    if (!pet.dobYear.trim()) errs.dobYear = true;
    if (!pet.spayedNeutered) errs.spayedNeutered = true;
    if (!pet.color.trim()) errs.color = true;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function validateExistingPet(): boolean {
    if (!selectedExistingPetId) {
      setExistingPetError('Please select a pet.');
      return false;
    }
    return true;
  }

  function validatePetStep(): boolean {
    return petMode === 'existing' ? validateExistingPet() : validatePet();
  }

  // Step 3: Validate adopter info (only when fields are filled)
  function validateAdopter(): boolean {
    const errs: Record<string, boolean> = {};
    if (!adopter.firstName.trim()) errs.firstName = true;
    if (!adopter.lastName.trim()) errs.lastName = true;
    if (!adopter.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adopter.email)) errs.email = true;
    if (!adopter.phone.trim()) errs.phone = true;
    if (!adopter.street.trim()) errs.street = true;
    if (!adopter.city.trim()) errs.city = true;
    if (!adopter.state) errs.state = true;
    if (!adopter.zip.trim() || !/^\d{5}$/.test(adopter.zip)) errs.zip = true;
    setAdopterErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function hasAdopterData(): boolean {
    return !!(adopter.firstName || adopter.lastName || adopter.email);
  }

  async function handleSaveAndClose() {
    if (!validatePetStep()) return;
    setSubmitting(true);
    try {
      if (petMode === 'existing') {
        await onRegister({
          graphqlId,
          chipId: chipId.trim(),
          serialNumber,
          needsClaim,
          existingPetId: selectedExistingPetId,
        });
      } else {
        await onRegister({
          graphqlId,
          chipId: chipId.trim(),
          serialNumber,
          needsClaim,
          petData: pet,
          breedId: pet.breed || null,
        });
      }
      handleClose();
    } catch (err) {
      showToast(`Failed to register chip: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleClaimAndClose() {
    setSubmitting(true);
    try {
      await onRegister({ graphqlId, chipId: chipId.trim(), serialNumber, needsClaim });
      handleClose();
    } catch (err) {
      showToast(`Failed to register chip: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setSubmitting(false);
    }
  }

  function handleNextToOwner() {
    if (!validatePetStep()) return;
    setStep(3);
  }

  function handleNextToConfirm() {
    if (hasAdopterData() && !validateAdopter()) return;
    setStep(4);
  }

  function handleSkipOwner() {
    setAdopter({ ...INITIAL_ADOPTER });
    setAdopterErrors({});
    setStep(4);
  }

  async function handleConfirmRegister() {
    // Three pet-source paths:
    //  - hasPet: chip already has a pet attached → don't touch the pet, just
    //    add the owner.
    //  - petMode === 'existing': link the chip to a pet from the rescue's
    //    own roster — backend resolves via existingPetId, no petData/breedId.
    //  - petMode === 'new' (default): create a new pet from the form.
    const usingExisting = !hasPet && petMode === 'existing';
    // Re-validate the selection at submit time: if the rescuePets cache
    // refetched between step 2 and step 4 and removed the chosen pet,
    // `selectedExistingPet` will be null even though `selectedExistingPetId`
    // still holds the stale id. Bail back to step 2 instead of silently
    // submitting against a pet the operator never reviewed.
    if (usingExisting && !selectedExistingPet) {
      setExistingPetError('That pet is no longer available — please pick another.');
      setStep(2);
      return;
    }
    setSubmitting(true);
    try {
      const adopterData = hasAdopterData() ? adopter : undefined;
      const petData = hasPet || usingExisting ? undefined : pet;
      const breedId = hasPet || usingExisting ? null : pet.breed || null;
      const existingPetId = usingExisting ? selectedExistingPetId : undefined;
      await onRegister({
        graphqlId,
        chipId: chipId.trim(),
        serialNumber,
        needsClaim,
        petData,
        breedId,
        adopterData,
        existingPetId,
      });
      handleClose();
    } catch (err) {
      showToast(`Failed to register chip: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setSubmitting(false);
    }
  }

  function updatePet<K extends keyof PetFormData>(key: K, value: PetFormData[K]) {
    setPet((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }

  function updateAdopter<K extends keyof AdopterFormData>(key: K, value: string) {
    setAdopter((prev) => ({ ...prev, [key]: value }));
    if (adopterErrors[key]) {
      setAdopterErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }

  // Format DOB for display
  function formatDob(): string {
    const parts: string[] = [];
    const m = MONTHS.find((mo) => mo.value === pet.dobMonth);
    if (m && m.value) parts.push(m.label);
    if (pet.dobDay) parts.push(pet.dobDay);
    if (pet.dobYear) parts.push(pet.dobYear);
    return parts.join(' ') || '\u2014';
  }

  function lowerOrEmpty(v?: string | null): string {
    return v ? v.toLowerCase() : '';
  }

  function existingPetSummaryFields(p: RescuePetGql) {
    const weightLbs = typeof p.weight === 'number' ? `${Math.round(p.weight * KG_TO_LBS)} lbs` : '\u2014';
    return [
      { label: 'Name', value: p.name || '\u2014' },
      { label: 'Species', value: lowerOrEmpty(p.species) || '\u2014' },
      { label: 'Breed', value: p.breed?.name || '\u2014' },
      { label: 'Gender', value: lowerOrEmpty(p.gender) || '\u2014' },
      { label: 'Date of Birth', value: formatDobParts(p.yearOfBirth, p.monthOfBirth, p.dayOfBirth) || '\u2014' },
      { label: 'Color/Markings', value: p.color || '\u2014' },
      { label: 'Weight', value: weightLbs },
    ];
  }

  return (
    <Modal open={open} onClose={handleClose} size="wide">
      <StepProgress currentStep={hasPet && step > 1 ? step - 1 : step} totalSteps={hasPet ? 3 : 4} />

      {/* Step 1: Chip ID */}
      {step === 1 && (
        <div>
          <ModalTitle>Register a chip</ModalTitle>
          <p className={styles.modalDescription}>
            Enter the short code or full microchip number from the chip packaging.
          </p>
          <PortalField label="Chip ID" error={chipError} showError={!!chipError}>
            <PortalInput
              value={chipId}
              onChange={(e) => {
                setChipId(e.target.value);
                setChipError('');
              }}
              onKeyDown={(e) => e.key === 'Enter' && validateChipId()}
              placeholder="e.g. FN-900001 or full 15-digit number"
              hasError={!!chipError}
              autoFocus
            />
          </PortalField>
          <ModalActions>
            <Button variant="green" onClick={validateChipId} disabled={validating}>
              {validating ? 'Checking...' : 'Continue'}
            </Button>
          </ModalActions>
        </div>
      )}

      {/* Step 2: Pet Info */}
      {step === 2 && (
        <div>
          <ModalTitle>Pet information</ModalTitle>
          <p className={styles.modalDescription}>
            {petMode === 'existing'
              ? 'Select an existing pet from your rescue to link this chip to.'
              : 'Enter the pet’s details for chip registration.'}
          </p>
          {/* Keep the toggle visible while petMode is 'existing' even if the
              options list is empty — otherwise a background refetch that
              empties rescuePetOptions traps the user in existing-mode with
              no way back to the new-pet form. */}
          {(rescuePetOptions.length > 0 || petMode === 'existing') && (
            <PortalField label="Pet source">
              <ToggleGroup
                options={[
                  { value: 'new', label: 'New pet' },
                  { value: 'existing', label: 'Existing rescue pet' },
                ]}
                value={petMode}
                onChange={(v) => {
                  const next = v as 'new' | 'existing';
                  setPetMode(next);
                  // Clear the opposite mode's state when switching, so a
                  // stale half-filled new-pet form or stale existing-pet
                  // selection doesn't quietly carry over and get submitted
                  // against the user's now-changed intent.
                  if (next === 'new') {
                    setSelectedExistingPetId('');
                    setExistingPetError('');
                  } else {
                    setPet({ ...INITIAL_PET });
                    setErrors({});
                  }
                }}
              />
            </PortalField>
          )}
          {petMode === 'existing' ? (
            <PortalField label="Pet *" error={existingPetError} showError={!!existingPetError}>
              <PortalCombobox
                options={existingPetComboboxOptions}
                value={selectedExistingPetId}
                onChange={(v) => {
                  setSelectedExistingPetId(v);
                  setExistingPetError('');
                }}
                placeholder="Search pets..."
              />
            </PortalField>
          ) : (
            <FormGrid>
              <PortalField label="Pet Name *" error="Required" showError={errors.petName}>
                <PortalInput
                  value={pet.petName}
                  onChange={(e) => updatePet('petName', e.target.value)}
                  placeholder="e.g. Biscuit"
                  hasError={errors.petName}
                />
              </PortalField>

              <PortalField label="Species">
                <ToggleGroup
                  options={[
                    { value: 'dog', label: 'Dog' },
                    { value: 'cat', label: 'Cat' },
                    { value: 'other', label: 'Other' },
                  ]}
                  value={pet.species}
                  onChange={(v) => {
                    updatePet('species', v);
                    updatePet('breed', '');
                  }}
                />
              </PortalField>

              <PortalField label="Breed">
                <BreedSelectModal
                  options={breedOptions}
                  value={pet.breed}
                  setValue={(v) => updatePet('breed', v)}
                  placeholder="e.g. Labrador Retriever"
                  variant="portal"
                />
                <CheckboxInline
                  id="mixed-breed"
                  label="Mixed breed"
                  checked={pet.mixedBreed}
                  onChange={(v) => updatePet('mixedBreed', v)}
                />
              </PortalField>

              <PortalField label="Gender *" error="Required" showError={errors.gender}>
                <ToggleGroup
                  options={[
                    { value: 'male', label: 'Male' },
                    { value: 'female', label: 'Female' },
                  ]}
                  value={pet.gender}
                  onChange={(v) => updatePet('gender', v)}
                />
              </PortalField>

              <PortalField
                label="Date of Birth *"
                hint="Year is required. Month and day are optional."
                error="Year is required"
                showError={errors.dobYear}
                fullWidth
              >
                <div style={{ display: 'flex', gap: 8 }}>
                  <PortalSelect
                    value={pet.dobYear}
                    onChange={(e) => updatePet('dobYear', e.target.value)}
                    style={{ flex: 1 }}
                    hasError={errors.dobYear}
                  >
                    {YEARS.map((y) => (
                      <option key={y.value} value={y.value}>
                        {y.label}
                      </option>
                    ))}
                  </PortalSelect>
                  <PortalSelect
                    value={pet.dobMonth}
                    onChange={(e) => updatePet('dobMonth', e.target.value)}
                    style={{ flex: 1 }}
                  >
                    {MONTHS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </PortalSelect>
                  <PortalSelect
                    value={pet.dobDay}
                    onChange={(e) => updatePet('dobDay', e.target.value)}
                    style={{ flex: 1 }}
                  >
                    {DAYS.map((d) => (
                      <option key={d.value} value={d.value}>
                        {d.label}
                      </option>
                    ))}
                  </PortalSelect>
                </div>
              </PortalField>

              <PortalField label="Spayed / Neutered *" error="Required" showError={errors.spayedNeutered}>
                <ToggleGroup
                  options={[
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' },
                  ]}
                  value={pet.spayedNeutered}
                  onChange={(v) => updatePet('spayedNeutered', v)}
                />
              </PortalField>

              <PortalField label="Color / Markings *" error="Required" showError={errors.color}>
                <PortalInput
                  value={pet.color}
                  onChange={(e) => updatePet('color', e.target.value)}
                  placeholder="e.g. Brown"
                  hasError={errors.color}
                />
              </PortalField>

              <PortalField label="Weight (lbs)">
                <PortalInput
                  value={pet.weight}
                  onChange={(e) => updatePet('weight', e.target.value)}
                  placeholder="e.g. 55"
                  type="number"
                />
              </PortalField>

              <PortalField label="Additional Info" fullWidth>
                <PortalTextarea
                  value={pet.additionalInfo}
                  onChange={(e) => updatePet('additionalInfo', e.target.value)}
                  placeholder="Any additional notes..."
                  rows={3}
                />
              </PortalField>
            </FormGrid>
          )}

          <ModalActions>
            <Button variant="secondary" onClick={() => setStep(1)}>
              Back
            </Button>
            <Button variant="dark" onClick={handleSaveAndClose} disabled={submitting}>
              {submitting ? 'Saving...' : 'Save & Close'}
            </Button>
            <Button variant="green" onClick={handleNextToOwner}>
              Continue
            </Button>
          </ModalActions>
        </div>
      )}

      {/* Step 3: Owner Info (optional) */}
      {step === 3 && (
        <div>
          <ModalTitle>Owner information (optional)</ModalTitle>
          <p className={styles.modalDescription}>
            Enter the adopter&apos;s contact information, or skip to register without an owner.
          </p>
          <FormGrid>
            <PortalField label="First Name *" error="Required" showError={adopterErrors.firstName}>
              <PortalInput
                value={adopter.firstName}
                onChange={(e) => updateAdopter('firstName', e.target.value)}
                placeholder="First name"
                hasError={adopterErrors.firstName}
              />
            </PortalField>

            <PortalField label="Last Name *" error="Required" showError={adopterErrors.lastName}>
              <PortalInput
                value={adopter.lastName}
                onChange={(e) => updateAdopter('lastName', e.target.value)}
                placeholder="Last name"
                hasError={adopterErrors.lastName}
              />
            </PortalField>

            <PortalField label="Email *" error="Valid email required" showError={adopterErrors.email}>
              <PortalInput
                value={adopter.email}
                onChange={(e) => updateAdopter('email', e.target.value)}
                placeholder="Email address"
                type="email"
                hasError={adopterErrors.email}
              />
            </PortalField>

            <PortalField label="Phone *" error="Required" showError={adopterErrors.phone}>
              <PortalInput
                value={adopter.phone}
                onChange={(e) => updateAdopter('phone', e.target.value)}
                placeholder="Phone number"
                type="tel"
                hasError={adopterErrors.phone}
              />
            </PortalField>

            <PortalField label="Street Address *" error="Required" showError={adopterErrors.street} fullWidth>
              <PortalInput
                value={adopter.street}
                onChange={(e) => updateAdopter('street', e.target.value)}
                placeholder="Street address"
                hasError={adopterErrors.street}
              />
            </PortalField>

            <PortalField label="Apt / Suite">
              <PortalInput
                value={adopter.apt}
                onChange={(e) => updateAdopter('apt', e.target.value)}
                placeholder="Apt / Suite"
              />
            </PortalField>

            <PortalField label="City *" error="Required" showError={adopterErrors.city}>
              <PortalInput
                value={adopter.city}
                onChange={(e) => updateAdopter('city', e.target.value)}
                placeholder="City"
                hasError={adopterErrors.city}
              />
            </PortalField>

            <PortalField label="State *" error="Required" showError={adopterErrors.state}>
              <PortalSelect
                value={adopter.state}
                onChange={(e) => updateAdopter('state', e.target.value)}
                hasError={adopterErrors.state}
              >
                <option value="">Select state</option>
                {US_STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </PortalSelect>
            </PortalField>

            <PortalField label="Zip Code *" error="5-digit zip required" showError={adopterErrors.zip}>
              <PortalInput
                value={adopter.zip}
                onChange={(e) => updateAdopter('zip', e.target.value)}
                placeholder="Zip code"
                hasError={adopterErrors.zip}
              />
            </PortalField>
          </FormGrid>

          <ModalActions>
            <Button variant="secondary" onClick={() => setStep(hasPet ? 1 : 2)}>
              Back
            </Button>
            {hasPet ? (
              <Button variant="dark" onClick={handleClaimAndClose} disabled={submitting}>
                {submitting ? 'Saving...' : 'Save & Close'}
              </Button>
            ) : (
              <Button variant="secondary" onClick={handleSkipOwner}>
                Skip
              </Button>
            )}
            <Button variant="green" onClick={handleNextToConfirm}>
              Continue
            </Button>
          </ModalActions>
        </div>
      )}

      {/* Step 4: Confirm */}
      {step === 4 && (
        <div>
          <ModalTitle>Confirm registration</ModalTitle>
          <p className={styles.modalDescription}>Review the details below before registering this chip.</p>
          <ConfirmSummary
            sections={[
              {
                title: 'Chip',
                fields: [{ label: 'Chip ID', value: chipId.trim() }],
              },
              {
                title: 'Pet Information',
                fields: hasPet
                  ? [
                      { label: 'Name', value: existingPet.name || '\u2014' },
                      { label: 'Species', value: existingPet.species || '\u2014' },
                      {
                        label: 'Breed',
                        value: existingPet.breedName
                          ? `${existingPet.breedName}${!existingPet.isPurebred ? ' (mixed)' : ''}`
                          : '\u2014',
                      },
                      { label: 'Gender', value: existingPet.gender || '\u2014' },
                      { label: 'Date of Birth', value: existingPet.dob || '\u2014' },
                      { label: 'Color/Markings', value: existingPet.color || '\u2014' },
                      { label: 'Weight', value: existingPet.weight || '\u2014' },
                    ]
                  : petMode === 'existing' && selectedExistingPet
                    ? existingPetSummaryFields(selectedExistingPet)
                    : [
                        { label: 'Name', value: pet.petName },
                        { label: 'Species', value: pet.species },
                        {
                          label: 'Breed',
                          value: (() => {
                            const name = breedsList.find((b: { id: string }) => b.id === pet.breed)?.name;
                            return name ? `${name}${pet.mixedBreed ? ' (mixed)' : ''}` : '\u2014';
                          })(),
                        },
                        { label: 'Gender', value: pet.gender },
                        { label: 'Date of Birth', value: formatDob() },
                        { label: 'Spayed/Neutered', value: pet.spayedNeutered },
                        { label: 'Color/Markings', value: pet.color },
                        { label: 'Weight', value: pet.weight ? `${pet.weight} lbs` : '\u2014' },
                      ],
              },
              ...(hasAdopterData()
                ? [
                    {
                      title: 'Owner Information',
                      fields: [
                        { label: 'Name', value: `${adopter.firstName} ${adopter.lastName}` },
                        { label: 'Email', value: adopter.email },
                        { label: 'Phone', value: adopter.phone },
                        {
                          label: 'Address',
                          value: [
                            adopter.street,
                            adopter.apt ? `Apt ${adopter.apt}` : '',
                            `${adopter.city}, ${adopter.state} ${adopter.zip}`,
                          ]
                            .filter(Boolean)
                            .join(', '),
                        },
                      ],
                    },
                  ]
                : []),
            ]}
          />
          <ModalActions>
            <Button variant="secondary" onClick={() => setStep(3)}>
              Back
            </Button>
            <Button variant="green" onClick={handleConfirmRegister} disabled={submitting}>
              {submitting ? 'Registering...' : 'Register Chip'}
            </Button>
          </ModalActions>
        </div>
      )}
    </Modal>
  );
}
